import React, { useState } from 'react';
import CustomerInfo from './steps/CustomerInfo';
import OrderDetails from './steps/OrderDetails';
import DeliveryInfo from './steps/DeliveryInfo';
import OrderSummary from './steps/OrderSummary';
import ProgressBar from './components/ProgressBar';
import { uploadFiles, uploadSnapshot } from '../../utils/uploadHelper';
import { sendOrderNotification } from '../../utils/emailHelper';
import { v4 as uuidv4 } from 'uuid';

const steps = [
  { id: 'customer', title: 'Customer Information' },
  { id: 'order', title: 'Order Details' },
  { id: 'delivery', title: 'Delivery Options' },
  { id: 'summary', title: 'Review & Submit' }
];

export default function StartProject() {
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    orderItems: [],
    artwork: {
      front: [],
      back: []
    },
    notes: {
      front: '',
      back: ''
    },
    deliveryMethod: 'shipping',
    shippingAddress: {
      street: '',
      city: '',
      state: '',
      zip: ''
    }
  });

  const [currentItem, setCurrentItem] = useState({
    type: 'T-Shirts',
    quantity: '',
    size: 'M',
    color: ''
  });

  const handleNext = (e) => {
    e.preventDefault();
    setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = (e) => {
    e.preventDefault();
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (submitting || currentStep !== steps.length - 1) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const orderId = uuidv4();
      const processedItems = [];

      // Process each order item
      for (const item of formData.orderItems) {
        const processedItem = { ...item };
        
        // Upload artwork files
        const uploadedFiles = [];
        if (item.artwork?.front?.length) {
          const frontFiles = await uploadFiles(
            item.artwork.front.map(f => f.file),
            orderId
          );
          uploadedFiles.push(...frontFiles);
        }
        
        if (item.artwork?.back?.length) {
          const backFiles = await uploadFiles(
            item.artwork.back.map(f => f.file),
            orderId
          );
          uploadedFiles.push(...backFiles);
        }
        
        processedItem.uploadedFiles = uploadedFiles;

        // Upload preview if available
        if (item.snapshots?.front) {
          processedItem.preview = await uploadSnapshot(
            item.snapshots.front,
            orderId,
            'front'
          );
        }

        processedItems.push(processedItem);
      }

      // Send email notifications
      await sendOrderNotification({
        orderId,
        name: formData.name,
        email: formData.email,
        company: formData.company,
        phone: formData.phone,
        orderItems: processedItems,
        delivery: {
          method: formData.deliveryMethod,
          shippingAddress: formData.shippingAddress
        }
      });

      setSubmitted(true);
    } catch (error) {
      console.error('Submission failed:', error);
      setSubmitError(
        `Failed to submit order: ${error.message}. Please try again or contact us directly.`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return formData.name && formData.email;
      case 1:
        return formData.orderItems.length > 0;
      case 2:
        if (formData.deliveryMethod === 'shipping') {
          const { street, city, state, zip } = formData.shippingAddress;
          return street && city && state && zip;
        }
        return true;
      case 3:
        return true;
      default:
        return false;
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <CustomerInfo 
            formData={formData}
            setFormData={setFormData}
          />
        );
      case 1:
        return (
          <OrderDetails
            formData={formData}
            setFormData={setFormData}
            currentItem={currentItem}
            setCurrentItem={setCurrentItem}
          />
        );
      case 2:
        return (
          <DeliveryInfo
            formData={formData}
            setFormData={setFormData}
          />
        );
      case 3:
        return (
          <OrderSummary
            formData={formData}
          />
        );
      default:
        return null;
    }
  };

  if (submitted) {
    return (
      <div className="bg-gray-900 py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-800 p-8 rounded-xl text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="mt-4 text-2xl font-bold text-cyan-500">Order Submitted Successfully!</h2>
            <p className="mt-2 text-gray-300">
              Thank you for your order. We'll review your request and get back to you shortly with a quote.
            </p>
            <p className="mt-4 text-gray-400">
              A confirmation email will be sent to {formData.email}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 py-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-cyan-500">Start Your Project</h1>
          <p className="mt-4 text-xl text-gray-300">
            Tell us about your custom apparel needs
          </p>
        </div>

        <ProgressBar 
          steps={steps} 
          currentStep={currentStep} 
        />

        <div className="mt-8 space-y-8">
          <div className="bg-gray-800 p-8 rounded-xl">
            {submitError && (
              <div className="bg-red-500/10 border border-red-500 rounded-lg p-4 text-red-500 mb-6">
                {submitError}
              </div>
            )}

            {renderStep()}
          </div>

          <div className="flex items-center justify-between pt-4">
            <button
              type="button"
              onClick={handleBack}
              disabled={currentStep === 0 || submitting}
              className="px-6 py-2 border border-transparent text-base font-medium rounded-md text-gray-300 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Back
            </button>

            {currentStep === steps.length - 1 ? (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canProceed() || submitting}
                className="px-6 py-2 border border-transparent text-base font-medium rounded-md text-black bg-cyan-500 hover:bg-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting...' : 'Submit Order'}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleNext}
                disabled={!canProceed() || submitting}
                className="px-6 py-2 border border-transparent text-base font-medium rounded-md text-black bg-cyan-500 hover:bg-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}